import { api, affiliateApi, brandApi } from "../lib/axios";
import type {
  ProgramPageDocument,
  ProgramPageProgram,
  ProgramPageStanding,
} from "@/types/programPage";

export type ProgramPageVisibility = "PUBLIC" | "AFFILIATES";

/** The brand's stored page. Null until the first save. */
export interface ProgramPageState {
  draft: ProgramPageDocument | null;
  status: "DRAFT" | "PUBLISHED";
  visibility: ProgramPageVisibility;
  navLabel: string;
  navIcon: string;
  publishedVersion: number;
  publishedAt: string | null;
  updatedAt: string;
  hasUnpublishedChanges: boolean;
}

/** GET /program-page/me */
export interface ProgramPageEditorBootstrap {
  /** False renders the upgrade prompt instead of the editor. */
  entitled: boolean;
  /** Entitled but no portal address yet: the brand can design, not serve. */
  portalLive: boolean;
  pageUrl: string | null;
  brand: {
    username: string | null;
    companyName: string | null;
    logo: string | null;
    primaryColour: string | null;
  };
  page: ProgramPageState | null;
  programs: ProgramPageProgram[];
}

export interface ProgramPageBrand {
  username: string | null;
  companyName: string | null;
  logo: string | null;
  joinUrl: string;
}

export interface ProgramPageView {
  restricted: false;
  brand: ProgramPageBrand;
  navLabel: string;
  document: ProgramPageDocument;
  /** What a tiers block with `programId: null` resolves to for this viewer. */
  defaultProgramId: string | null;
  programs: ProgramPageProgram[];
  /** Keyed by program id. Empty for visitors. */
  standing: Record<string, ProgramPageStanding>;
  isMember?: boolean;
}

/** A members-only page, seen from outside: the brand's name and nothing else. */
export interface RestrictedProgramPageView {
  restricted: true;
  brand: ProgramPageBrand;
}

export interface ProgramPageNavItem {
  brandUsername: string;
  label: string;
  /** A lucide icon name. */
  icon: string;
}

export const programPageService = {
  async getMine(): Promise<ProgramPageEditorBootstrap> {
    const { data } = await brandApi.get("/program-page/me");
    return data;
  },

  async saveDraft(document: ProgramPageDocument): Promise<ProgramPageState> {
    const { data } = await brandApi.put("/program-page/me/draft", { document });
    return data;
  },

  async updateSettings(settings: {
    visibility?: ProgramPageVisibility;
    navLabel?: string;
    navIcon?: string;
  }): Promise<ProgramPageState> {
    const { data } = await brandApi.put("/program-page/me/settings", settings);
    return data;
  },

  async publish(document: ProgramPageDocument): Promise<ProgramPageState> {
    const { data } = await brandApi.post("/program-page/me/publish", { document });
    return data;
  },

  async unpublish(): Promise<ProgramPageState> {
    const { data } = await brandApi.post("/program-page/me/unpublish");
    return data;
  },

  async getPublic(username: string): Promise<ProgramPageView | RestrictedProgramPageView> {
    const { data } = await api.get(
      `/program-page/public/${encodeURIComponent(username)}`
    );
    return data;
  },

  async getForAffiliate(username: string): Promise<ProgramPageView> {
    const { data } = await affiliateApi.get(
      `/program-page/affiliate/${encodeURIComponent(username)}`
    );
    return data;
  },

  async getAffiliateNav(): Promise<{ item: ProgramPageNavItem | null }> {
    const { data } = await affiliateApi.get("/program-page/affiliate/nav");
    return data;
  },
};
